import React from 'react';
import { Modal, Button, Tag, Typography, Space, Row, Col } from 'antd';
import {
  RocketOutlined,
  CrownOutlined,
  CustomerServiceOutlined,
  LockOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { UpgradePayload } from '../contexts/UpgradeModalContext';
import { useAppContext } from '../contexts/AppContext';
import { useRole } from '../hooks/useRole';
import { T } from '../styles/theme';

const { Title, Text, Paragraph } = Typography;

interface UpgradeModalProps {
  open: boolean;
  payload: UpgradePayload;
  onClose: () => void;
}

const ERROR_LABELS: Record<string, { title: string; icon: React.ReactNode }> = {
  TrialExpired: {
    title: 'Trial Expired',
    icon: <ExclamationCircleOutlined style={{ color: '#fff', fontSize: 20 }} />,
  },
  SubscriptionSuspended: {
    title: 'Subscription Suspended',
    icon: <ExclamationCircleOutlined style={{ color: '#fff', fontSize: 20 }} />,
  },
  PlanUpgradeRequired: {
    title: 'Upgrade Required',
    icon: <LockOutlined style={{ color: '#fff', fontSize: 20 }} />,
  },
};

const BENEFITS = [
  'Access all premium features',
  'Priority support',
  'Unlimited users & data',
];

const UpgradeModal: React.FC<UpgradeModalProps> = ({ open, payload, onClose }) => {
  const navigate = useNavigate();
  const { subscription } = useAppContext();
  const { isAdmin } = useRole();

  const meta = payload.errorCode ? ERROR_LABELS[payload.errorCode] : undefined;
  const currentPlan = payload.currentPlan || subscription?.planCode || 'Current Plan';
  const requiredPlan = payload.requiredPlan;
  const feature = payload.feature;
  const description =
    payload.description ||
    payload.message ||
    'This feature requires a higher subscription plan. Upgrade to unlock full access.';

  const handleUpgrade = () => {
    onClose();
    navigate('/v1/admin/subscription');
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      centered
      styles={{
        content: { borderRadius: 18, padding: 0, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
        mask: { backdropFilter: 'blur(3px)', background: 'rgba(15, 23, 32, 0.55)' },
      }}
    >
      {/* Gradient header — app teal theme */}
      <div
        style={{
          background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
          padding: '30px 32px 26px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -30, right: -20, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ position: 'absolute', bottom: -40, left: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, position: 'relative' }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.28)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {meta?.icon ?? <RocketOutlined style={{ color: '#fff', fontSize: 20 }} />}
          </div>
          <div>
            <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', display: 'block' }}>
              {payload.errorCode ? 'Access Restricted' : 'Subscription'}
            </Text>
            <Title level={4} style={{ color: '#fff', margin: 0, fontSize: 19, fontWeight: 700 }}>
              {meta?.title ?? 'Upgrade Your Plan'}
            </Title>
          </div>
        </div>

        {/* Plan chips */}
        <Space wrap size={10} align="center" style={{ position: 'relative' }}>
          <Tag style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.28)', color: '#fff', borderRadius: 20, padding: '3px 14px', fontSize: 12.5, fontWeight: 500 }}>
            Current: {currentPlan}
          </Tag>
          {requiredPlan && (
            <>
              <ArrowRightOutlined style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }} />
              <Tag style={{ background: '#fff', border: 'none', color: T.primaryDark, borderRadius: 20, padding: '3px 14px', fontSize: 12.5, fontWeight: 700 }}>
                <CrownOutlined style={{ marginRight: 5 }} />
                {requiredPlan}
              </Tag>
            </>
          )}
        </Space>
      </div>

      {/* Body */}
      <div style={{ padding: '26px 32px 28px' }}>
        {feature && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 14,
              background: T.primaryLighter,
              border: `1px solid ${T.primary}22`,
              borderRadius: 20,
              padding: '4px 12px 4px 10px',
            }}
          >
            <LockOutlined style={{ color: T.primary, fontSize: 12 }} />
            <Text strong style={{ color: T.text, fontSize: 12.5 }}>
              {feature}
            </Text>
          </div>
        )}

        <Paragraph style={{ color: T.textSecondary, fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
          {description}
        </Paragraph>

        {/* Benefits */}
        <div
          style={{
            background: T.primaryLighter,
            borderRadius: 12,
            padding: '14px 16px',
            marginBottom: 22,
            border: `1px solid ${T.primary}1f`,
          }}
        >
          {BENEFITS.map((b, i) => (
            <div
              key={b}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                marginBottom: i < BENEFITS.length - 1 ? 9 : 0,
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: T.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CheckCircleOutlined style={{ color: '#fff', fontSize: 11 }} />
              </span>
              <Text style={{ color: T.text, fontSize: 13.5 }}>{b}</Text>
            </div>
          ))}
        </div>

        <Row gutter={12}>
          {isAdmin ? (
            <Col span={14}>
              <Button
                type="primary"
                block
                size="large"
                icon={<RocketOutlined />}
                onClick={handleUpgrade}
                style={{
                  background: T.primary,
                  borderColor: T.primary,
                  borderRadius: 10,
                  fontWeight: 600,
                  height: 44,
                  boxShadow: `0 6px 16px ${T.primary}40`,
                }}
              >
                Upgrade Plan
              </Button>
            </Col>
          ) : (
            <Col span={14}>
              <Button
                type="primary"
                block
                size="large"
                icon={<CustomerServiceOutlined />}
                onClick={onClose}
                style={{
                  background: T.primary,
                  borderColor: T.primary,
                  borderRadius: 10,
                  fontWeight: 600,
                  height: 44,
                  boxShadow: `0 6px 16px ${T.primary}40`,
                }}
              >
                Contact Admin
              </Button>
            </Col>
          )}
          <Col span={10}>
            <Button
              block
              size="large"
              icon={<CustomerServiceOutlined />}
              href="mailto:sales@example.com"
              style={{ borderRadius: 10, color: T.text, height: 44, borderColor: T.border }}
              onClick={onClose}
            >
              Contact Sales
            </Button>
          </Col>
        </Row>

        <Button type="link" block onClick={onClose} style={{ marginTop: 10, color: T.textSecondary, fontSize: 12.5 }}>
          Maybe later
        </Button>
      </div>
    </Modal>
  );
};

export default UpgradeModal;
